import { useEffect, useState } from "react";
import { DEV_LOG_STREAMS, type DevLogStreamId } from "./api";
import { LogPane } from "./components/LogPane";
import { useDevLogs } from "./hooks/useDevLogs";

const PANES: Array<{ id: DevLogStreamId | "all"; title: string; subtitle: string }> = [
  { id: "api", title: "API", subtitle: "REST + WebSocket" },
  { id: "tcp", title: "TCP", subtitle: "JT808 :7611 · JT1078 :7614" },
  { id: "udp", title: "UDP", subtitle: "JT808 :7612" },
  { id: "mediamtx", title: "MediaMTX", subtitle: "RTSP/HLS/WebRTC server" },
  { id: "rtsp", title: "RTSP", subtitle: "ffmpeg publish" },
  { id: "all", title: "ALL", subtitle: "every stream" },
];

export default function DevConsole() {
  const { byStream, all, connected } = useDevLogs();
  const [full, setFull] = useState<DevLogStreamId | "all" | null>(null);

  useEffect(() => {
    document.title = "Dev logs — Dashcam";
    return () => {
      document.title = "Dashcam Fleet Tracking";
    };
  }, []);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setFull(null);
      if (ev.key >= "1" && ev.key <= "6") {
        const pane = PANES[Number(ev.key) - 1];
        if (pane) setFull((cur) => (cur === pane.id ? null : pane.id));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className={`dev-page ${full ? "has-full" : ""}`}>
      <header className="topbar">
        <div>
          <h1>Dev logs</h1>
          <p>API · TCP · UDP · MediaMTX · RTSP — click Full screen or press 1–6, Esc to restore</p>
        </div>
        <div className="topbar-actions">
          <span className={`ws ${connected ? "on" : "off"}`}>{connected ? "logs live" : "logs connecting"}</span>
          {full && (
            <button type="button" onClick={() => setFull(null)}>
              Restore split
            </button>
          )}
          <a className="topbar-link" href="/">
            Fleet dashboard
          </a>
        </div>
      </header>
      <div className={`dev-grid ${full ? "is-full" : ""}`}>
        {PANES.map((pane) => {
          if (full && full !== pane.id) return null;
          const events = pane.id === "all" ? all : byStream[pane.id];
          return (
            <LogPane
              key={pane.id}
              title={pane.title}
              subtitle={pane.subtitle}
              events={events}
              expanded={full === pane.id}
              onToggleExpand={() => setFull((cur) => (cur === pane.id ? null : pane.id))}
            />
          );
        })}
      </div>
      <p className="dev-hint">
        Streams: {DEV_LOG_STREAMS.join(" · ")} · one pane full screen at a time
      </p>
    </div>
  );
}
